import React, { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import { api, apiErr, API } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  Users, Ship, ClipboardList, ShieldCheck, ShieldAlert, TrendingUp, Anchor, Ban, Check,
  Trash2, RefreshCw, Activity, Euro, FileText, ArrowUpRight, MessageCircle, Send, X, AlertTriangle,
  Megaphone, Search, Mail, MousePointerClick, Eye, UserMinus
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StripeSettings from "@/components/StripeSettings";

const STATUS_COLOR = {
  open: "bg-[#EFE9E1] text-[#0B1D3A]",
  accepted: "bg-blue-100 text-blue-700",
  in_progress: "bg-[#FF6B57]/10 text-[#FF6B57]",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-gray-100 text-gray-500",
};

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [txs, setTxs] = useState([]);
  const [activity, setActivity] = useState([]);
  const [sTickets, setSTickets] = useState([]);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [outreachLeads, setOutreachLeads] = useState([]);
  const [outreachStats, setOutreachStats] = useState({});
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scrapeResult, setScrapeResult] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [manualPaste, setManualPaste] = useState("");
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/requests"),
      api.get("/admin/disputes"),
      api.get("/admin/kyc"),
      api.get("/admin/transactions"),
      api.get("/admin/activity"),
      api.get("/admin/support/tickets"),
      api.get("/admin/payouts/pending"),
      api.get("/admin/outreach/leads"),
      api.get("/admin/outreach/stats"),
    ]).then(([s, u, r, d, k, t, a, st, pp, ol, os]) => {
      setStats(s.data); setUsers(u.data); setReqs(r.data); setDisputes(d.data); setKyc(k.data); setTxs(t.data); setActivity(a.data); setSTickets(st.data); setPendingPayouts(pp.data);
      setOutreachLeads(ol.data); setOutreachStats(os.data);
    }).catch(e => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  const runScrape = async () => {
    if (!scrapeUrl.startsWith("http")) { toast.error("URL debe empezar por https://"); return; }
    setScraping(true);
    try {
      const { data } = await api.post("/admin/outreach/scrape", { url: scrapeUrl });
      setScrapeResult(data);
      toast.success(`Encontrados ${data.found} emails (${data.corporate.length} corporativos, ${data.personal.length} personales)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setScraping(false); }
  };

  const addLeads = async (emails, sourceUrl = "") => {
    if (!emails.length) return;
    try {
      const { data } = await api.post("/admin/outreach/leads", { emails, source_url: sourceUrl });
      toast.success(`${data.added} añadidos, ${data.skipped} ya existían/inválidos`);
      setScrapeResult(null); setScrapeUrl(""); setManualPaste("");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const importManual = () => {
    const list = manualPaste.split(/[\s,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    addLeads(list, "manual");
  };

  const toggleLead = (id) => {
    setSelectedLeads(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAllNew = () => {
    setSelectedLeads(new Set(outreachLeads.filter(l => l.status === "new").map(l => l.id)));
  };
  const clearSelection = () => setSelectedLeads(new Set());

  const sendCampaign = async () => {
    if (selectedLeads.size === 0) { toast.error("Selecciona al menos un lead"); return; }
    if (!confirm(`Enviar campaña a ${selectedLeads.size} contactos? (límite ${outreachStats.daily_cap || 200}/día, quedan ${outreachStats.remaining_today || 0} hoy)`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/outreach/send", { lead_ids: [...selectedLeads] });
      const msg = `Enviados: ${data.sent}` + (data.excluded_over_cap ? ` · Fuera del cap: ${data.excluded_over_cap}` : "") + (data.errors.length ? ` · Errores: ${data.errors.length}` : "");
      toast.success(msg);
      clearSelection();
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  const removeLead = async (id) => {
    if (!confirm("¿Eliminar este lead definitivamente?")) return;
    await api.delete(`/admin/outreach/leads/${id}`);
    load();
  };

  const userAction = async (uid, action, reason = "") => {
    if (action === "delete" && !confirm("¿Eliminar este usuario definitivamente?")) return;
    if (action === "suspend" && !confirm("¿Suspender esta cuenta?")) return;
    try { await api.patch(`/admin/users/${uid}`, { action, reason }); toast.success("Hecho"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const cancelReq = async (id) => {
    if (!confirm("¿Cancelar esta solicitud?")) return;
    await api.post(`/admin/requests/${id}/cancel`); toast.success("Cancelada"); load();
  };
  const refundReq = async (id) => {
    if (!confirm("¿Reembolsar al cliente? Se anula la garantía.")) return;
    await api.post(`/admin/requests/${id}/refund`); toast.success("Reembolsado"); load();
  };
  const resolveDisp = async (id, resolution) => {
    await api.post(`/admin/disputes/${id}/resolve`, { resolution }); toast.success("Resuelto"); load();
  };
  const kycAction = async (id, action) => {
    await api.patch(`/admin/kyc/${id}`, { action }); toast.success("Hecho"); load();
  };

  const openTicket = async (id) => {
    try { const { data } = await api.get(`/support/tickets/${id}`); setTicketDetail(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const replyTicket = async () => {
    if (!reply.trim() || !ticketDetail) return;
    await api.post(`/support/tickets/${ticketDetail.ticket.id}/messages`, { message: reply });
    setReply("");
    openTicket(ticketDetail.ticket.id); load();
  };
  const setTicketStatus = async (id, status) => {
    await api.patch(`/admin/support/tickets/${id}`, { status });
    toast.success(status === "closed" ? "Ticket cerrado" : "Ticket reabierto");
    if (ticketDetail?.ticket.id === id) openTicket(id);
    load();
  };

  const markPayoutPaid = async (id) => {
    const ref = prompt("Referencia de la transferencia bancaria (opcional):", "");
    if (ref === null) return;
    try {
      await api.post(`/admin/payouts/${id}/mark-paid`, { reference: ref });
      toast.success("Payout marcado como pagado");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totalRevenue = txs.filter(t => t.payment_status === "paid").reduce((s, t) => s + (t.amount || 0), 0);

  const cards = [
    { label: "Usuarios", val: stats.users, icon: Users },
    { label: "Propietarios", val: stats.owners, icon: Ship },
    { label: "Profesionales", val: stats.professionals, icon: ShieldCheck },
    { label: "En marcha", val: (stats.requests_accepted || 0) + (stats.requests_open || 0), icon: TrendingUp },
    { label: "Completadas", val: stats.requests_completed, icon: Anchor },
    { label: "Facturado", val: `${totalRevenue.toFixed(0)}€`, icon: Euro },
  ];

  return (
    <div className="min-h-screen hub-cream">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="hub-pill hub-sand text-[#0B1D3A] mb-3"><ShieldCheck size={12}/> Panel de administración</div>
        <h1 className="font-serif text-5xl text-[#0B1D3A]">Control total del puerto.</h1>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8">
          {cards.map((c) => (
            <div key={c.label} className="hub-card p-4">
              <c.icon size={16} className="hub-coral"/>
              <div className="text-xs uppercase tracking-widest text-[#5C6B7D] font-display mt-2">{c.label}</div>
              <div className="font-serif text-3xl mt-1">{c.val ?? 0}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="users" className="mt-10">
          <TabsList className="bg-white/60 border border-[#0B1D3A]/10 rounded-full p-1 flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="admin-tab-users" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Usuarios ({users.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Solicitudes ({reqs.length})</TabsTrigger>
            <TabsTrigger value="kyc" data-testid="admin-tab-kyc" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">KYC ({kyc.filter(d=>d.status==="pending").length})</TabsTrigger>
            <TabsTrigger value="disputes" data-testid="admin-tab-disputes" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Incidencias ({disputes.filter(d=>d.status==="open").length})</TabsTrigger>
            <TabsTrigger value="txs" data-testid="admin-tab-txs" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Transacciones ({txs.length})</TabsTrigger>
            <TabsTrigger value="support" data-testid="admin-tab-support" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Soporte ({sTickets.filter(t=>t.unread_admin).length})</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="admin-tab-payouts" className="rounded-full data-[state=active]:bg-[#FF6B57] data-[state=active]:text-white font-display">Payouts manuales ({pendingPayouts.length})</TabsTrigger>
            <TabsTrigger value="outreach" data-testid="admin-tab-outreach" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Captación ({outreachStats.total || 0})</TabsTrigger>
            <TabsTrigger value="stripe" data-testid="admin-tab-stripe" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Cobros</TabsTrigger>
            <TabsTrigger value="activity" data-testid="admin-tab-activity" className="rounded-full data-[state=active]:bg-[#0B1D3A] data-[state=active]:text-white font-display">Actividad</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {users.map((u) => (
                <div key={u.id} className="p-4 flex items-center gap-3" data-testid={`admin-user-${u.id}`}>
                  <div className="w-10 h-10 rounded-full hub-sand flex items-center justify-center font-serif">{u.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{u.name} {u.banned && <span className="hub-pill bg-red-100 text-red-700 ml-2">SUSPENDIDA</span>}</div>
                    <div className="text-xs text-[#5C6B7D] truncate">{u.email} · {u.role}</div>
                    {u.professional && <div className="text-xs text-[#5C6B7D]">{u.professional.rating || 0}★ · {u.professional.jobs_completed || 0} trabajos · {u.professional.verified ? "✓ verif" : "no verif"}</div>}
                  </div>
                  <div className="flex gap-1">
                    {u.professional && !u.professional.verified && <Button size="sm" variant="ghost" onClick={() => userAction(u.id, "verify")} className="text-emerald-600" data-testid={`verify-${u.id}`}><Check size={13}/></Button>}
                    {u.banned ? (
                      <Button size="sm" variant="ghost" onClick={() => userAction(u.id, "reactivate")} className="text-emerald-600" data-testid={`react-${u.id}`}><RefreshCw size={13}/></Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => userAction(u.id, "suspend", "Admin decision")} className="text-amber-600" data-testid={`susp-${u.id}`}><Ban size={13}/></Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => userAction(u.id, "delete")} className="text-red-500" data-testid={`del-${u.id}`}><Trash2 size={13}/></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requests" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {reqs.map((r) => (
                <div key={r.id} className="p-4" data-testid={`admin-req-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`hub-pill ${STATUS_COLOR[r.status] || STATUS_COLOR.open}`}>{r.status}</span>
                    {r.payment_status && <span className="hub-pill bg-emerald-100 text-emerald-700">{r.payment_status}</span>}
                    {r.urgent && <span className="hub-pill bg-[#FF6B57] text-white">Urgente</span>}
                    {r.has_dispute && <span className="hub-pill bg-red-100 text-red-700">Incidencia</span>}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <div>
                      <div className="font-display font-semibold text-sm">{r.service_type} · {r.port} · {r.owner_name}</div>
                      {r.professional_name && <div className="text-xs text-[#5C6B7D]">Prof: {r.professional_name}{r.final_price ? ` · ${r.final_price}€` : ""}</div>}
                    </div>
                    <div className="flex gap-1">
                      {r.status !== "cancelled" && r.status !== "completed" && <Button size="sm" variant="ghost" onClick={() => cancelReq(r.id)} className="text-amber-600" data-testid={`cancel-r-${r.id}`}>Cancelar</Button>}
                      {r.payment_status === "in_escrow" && <Button size="sm" variant="ghost" onClick={() => refundReq(r.id)} className="text-red-500" data-testid={`refund-r-${r.id}`}>Reembolsar</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="kyc" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {kyc.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Nada en cola.</div>}
              {kyc.map(d => (
                <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`admin-kyc-${d.id}`}>
                  <FileText className="text-[#5C6B7D]"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm">{d.user?.name} · {d.doc_type}</div>
                    <div className="text-xs text-[#5C6B7D]">{d.user?.email} · {new Date(d.created_at).toLocaleString("es-ES")}</div>
                  </div>
                  <a href={`${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" className="text-xs text-[#FF6B57] font-display flex items-center gap-1" data-testid={`view-kyc-${d.id}`}><ArrowUpRight size={11}/> Ver</a>
                  <span className="hub-pill hub-sand text-[#0B1D3A]">{d.status}</span>
                  {d.status === "pending" && (
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => kycAction(d.id, "approve")} className="hub-btn-primary" data-testid={`kyc-appr-${d.id}`}>Aprobar</Button>
                      <Button size="sm" variant="ghost" onClick={() => kycAction(d.id, "reject")} className="text-red-500" data-testid={`kyc-rej-${d.id}`}>Rechazar</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="disputes" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {disputes.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Sin incidencias.</div>}
              {disputes.map(d => (
                <div key={d.id} className="p-4" data-testid={`admin-disp-${d.id}`}>
                  <div className="flex items-center gap-2"><ShieldAlert className="text-red-500" size={14}/><span className="hub-pill bg-red-100 text-red-700">{d.status}</span></div>
                  <div className="font-display font-semibold text-sm mt-1">Motivo: {d.reason} — por {d.opener_role}</div>
                  {d.detail && <p className="text-sm text-[#0B1D3A]/80 mt-1">{d.detail}</p>}
                  {d.status === "open" && (
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => resolveDisp(d.id, "resolved")} className="hub-btn-primary" data-testid={`disp-res-${d.id}`}>Resolver a favor del pro</Button>
                      <Button size="sm" onClick={() => resolveDisp(d.id, "refunded")} className="hub-btn-coral hub-btn-primary" data-testid={`disp-ref-${d.id}`}>Reembolsar cliente</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="txs" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {txs.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Sin transacciones.</div>}
              {txs.map((t) => (
                <div key={t.session_id} className="p-4 flex items-center gap-3" data-testid={`admin-tx-${t.session_id}`}>
                  <Euro className="text-[#FF6B57]"/>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-sm">{t.amount}€ · {t.currency?.toUpperCase()}</div>
                    <div className="text-xs text-[#5C6B7D]">{new Date(t.created_at).toLocaleString("es-ES")} · {t.session_id?.slice(0, 25)}</div>
                  </div>
                  <span className={`hub-pill ${t.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : t.payment_status === "refunded" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-500"}`}>{t.payment_status}</span>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="support" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {sTickets.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Sin tickets aún.</div>}
              {sTickets.map(t => (
                <button key={t.id} onClick={() => openTicket(t.id)} className="p-4 flex items-center gap-3 w-full text-left" data-testid={`admin-tk-${t.id}`}>
                  <MessageCircle className="text-[#FF6B57]"/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`hub-pill ${t.status === "open" ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-500"}`}>{t.status}</span>
                      <span className="hub-pill hub-sand text-[#0B1D3A]">{t.category}</span>
                      {t.unread_admin && <span className="hub-pill bg-[#FF6B57] text-white">Nuevo</span>}
                    </div>
                    <div className="font-display font-semibold text-sm mt-1 truncate">{t.subject}</div>
                    <div className="text-xs text-[#5C6B7D]">{t.user_name} ({t.user_role}) · {new Date(t.updated_at).toLocaleString("es-ES")}</div>
                  </div>
                </button>
              ))}
            </div>
            <Dialog open={!!ticketDetail} onOpenChange={(o)=>!o && setTicketDetail(null)}>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader><DialogTitle className="font-serif text-2xl">{ticketDetail?.ticket?.subject}</DialogTitle></DialogHeader>
                {ticketDetail && (
                  <>
                    <div className="text-xs text-[#5C6B7D] pb-2 border-b border-[#0B1D3A]/10">
                      {ticketDetail.ticket.user_name} ({ticketDetail.ticket.user_email}) · {ticketDetail.ticket.category}
                    </div>
                    <div className="flex-1 overflow-y-auto py-3 space-y-2">
                      {ticketDetail.messages.map(m => (
                        <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 ${m.sender_role === "admin" ? "bg-[#0B1D3A] text-white" : "bg-white border border-[#0B1D3A]/10"}`}>
                            <div className="text-[10px] uppercase tracking-widest opacity-70 font-display mb-1">{m.sender_name} {m.sender_role === "admin" ? "· admin" : `· ${m.sender_role}`}</div>
                            <div className="text-sm whitespace-pre-wrap">{m.message}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {ticketDetail.ticket.status === "open" ? (
                      <>
                        <div className="flex gap-2 pt-2 border-t border-[#0B1D3A]/10">
                          <Input value={reply} onChange={e=>setReply(e.target.value)} onKeyDown={e=>e.key==="Enter" && replyTicket()} placeholder="Escribe tu respuesta..." className="rounded-full" data-testid="adm-reply-input"/>
                          <Button onClick={replyTicket} className="hub-btn-primary rounded-full gap-2" data-testid="adm-reply-btn"><Send size={14}/></Button>
                        </div>
                        <Button variant="ghost" onClick={() => setTicketStatus(ticketDetail.ticket.id, "closed")} className="text-red-500 text-xs mt-1" data-testid="adm-close-tk-btn"><X size={12}/> Cerrar ticket</Button>
                      </>
                    ) : (
                      <Button variant="ghost" onClick={() => setTicketStatus(ticketDetail.ticket.id, "open")} className="mt-2" data-testid="adm-reopen-tk-btn">Reabrir ticket</Button>
                    )}
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="stripe" className="mt-4">
            <StripeSettings/>
          </TabsContent>

          <TabsContent value="outreach" className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
              {[
                { label: "Total leads", val: outreachStats.total || 0, icon: Users, cls: "text-[#0B1D3A]" },
                { label: "Enviados hoy", val: `${outreachStats.sent_today || 0}/${outreachStats.daily_cap || 200}`, icon: Send, cls: "hub-coral" },
                { label: "Enviados total", val: outreachStats.sent_total || 0, icon: Mail, cls: "text-[#0B1D3A]" },
                { label: "Aperturas", val: `${outreachStats.opened || 0} (${outreachStats.open_rate || 0}%)`, icon: Eye, cls: "text-emerald-600" },
                { label: "Clicks", val: `${outreachStats.clicked || 0} (${outreachStats.click_rate || 0}%)`, icon: MousePointerClick, cls: "hub-coral" },
                { label: "Bajas", val: outreachStats.opted_out || 0, icon: UserMinus, cls: "text-red-500" },
              ].map((c) => (
                <div key={c.label} className="hub-card p-3">
                  <c.icon size={14} className={c.cls}/>
                  <div className="text-[10px] uppercase tracking-widest text-[#5C6B7D] font-display mt-1">{c.label}</div>
                  <div className="font-serif text-xl mt-0.5">{c.val}</div>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="hub-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="hub-coral" size={16}/>
                  <h3 className="font-serif text-xl">Scrapear directorio</h3>
                </div>
                <p className="text-xs text-[#5C6B7D] mb-3">Pega la URL de un directorio náutico público (federaciones, guías de puertos, listados de empresas). Extrae emails corporativos con user-agent identificado.</p>
                <div className="flex gap-2">
                  <Input value={scrapeUrl} onChange={e => setScrapeUrl(e.target.value)} placeholder="https://directorio-nautico.es/empresas"
                    className="rounded-full text-sm" data-testid="outreach-scrape-url"/>
                  <Button onClick={runScrape} disabled={scraping} className="hub-btn-primary rounded-full gap-2" data-testid="outreach-scrape-btn">
                    <Search size={14}/> {scraping ? "..." : "Escanear"}
                  </Button>
                </div>
                {scrapeResult && (
                  <div className="mt-3 space-y-2">
                    <div className="text-xs text-[#5C6B7D]">Encontrados: <b className="text-[#0B1D3A]">{scrapeResult.found}</b> · corporativos: <b className="text-emerald-600">{scrapeResult.corporate.length}</b> · personales (Gmail/Hotmail): <b className="text-amber-600">{scrapeResult.personal.length}</b></div>
                    <div className="max-h-40 overflow-y-auto p-2 bg-[#FBF9F5] rounded text-xs font-mono">
                      {scrapeResult.corporate.map(e => <div key={e} className="text-emerald-700">✓ {e}</div>)}
                      {scrapeResult.personal.map(e => <div key={e} className="text-amber-600">⚠ {e}</div>)}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => addLeads(scrapeResult.corporate, scrapeUrl)} className="hub-btn-primary rounded-full text-xs" data-testid="outreach-add-corp">
                        Añadir solo corporativos ({scrapeResult.corporate.length})
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => addLeads([...scrapeResult.corporate, ...scrapeResult.personal], scrapeUrl)} className="text-xs" data-testid="outreach-add-all">
                        Añadir todos
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="hub-card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="text-[#0B1D3A]" size={16}/>
                  <h3 className="font-serif text-xl">Importar manualmente</h3>
                </div>
                <p className="text-xs text-[#5C6B7D] mb-3">Pega una lista de emails separados por coma, punto y coma o salto de línea.</p>
                <Textarea value={manualPaste} onChange={e => setManualPaste(e.target.value)}
                  placeholder="info@puertodeportivobcn.es, contacto@nauticapalma.com, hola@varaderotarragona.com..."
                  className="text-xs font-mono h-24" data-testid="outreach-manual-paste"/>
                <Button onClick={importManual} className="hub-btn-primary rounded-full mt-3 gap-2" data-testid="outreach-import-manual">
                  <Mail size={14}/> Importar leads
                </Button>
              </div>
            </div>

            <div className="hub-card">
              <div className="p-4 border-b border-[#0B1D3A]/10 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Megaphone className="hub-coral" size={16}/>
                  <span className="font-serif text-lg">Contactos ({outreachLeads.length})</span>
                  {selectedLeads.size > 0 && <span className="hub-pill bg-[#FF6B57] text-white text-[10px]">{selectedLeads.size} seleccionados</span>}
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="ghost" onClick={selectAllNew} className="text-xs rounded-full" data-testid="outreach-select-new">
                    Seleccionar todos los nuevos
                  </Button>
                  {selectedLeads.size > 0 && (
                    <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs rounded-full">
                      Deseleccionar
                    </Button>
                  )}
                  <Button size="sm" onClick={sendCampaign} disabled={sending || selectedLeads.size === 0}
                    className="hub-btn-primary rounded-full gap-2 text-xs" data-testid="outreach-send-btn">
                    <Send size={12}/> {sending ? "Enviando..." : `Enviar campaña (${selectedLeads.size})`}
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-[#0B1D3A]/5 max-h-[500px] overflow-y-auto">
                {outreachLeads.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Sin contactos aún. Empieza por escanear un directorio o importar una lista arriba.</div>}
                {outreachLeads.map(l => {
                  const statusColor = {
                    new: "bg-[#EFE9E1] text-[#0B1D3A]",
                    sent: "bg-blue-100 text-blue-700",
                    opened: "bg-emerald-100 text-emerald-700",
                    clicked: "bg-[#FF6B57]/10 text-[#FF6B57]",
                    opted_out: "bg-red-100 text-red-700",
                  }[l.status] || "bg-gray-100 text-gray-500";
                  const isSelectable = l.status === "new" && !l.opt_out_at;
                  return (
                    <div key={l.id} className="p-3 flex items-center gap-3" data-testid={`outreach-lead-${l.id}`}>
                      {isSelectable ? (
                        <Checkbox checked={selectedLeads.has(l.id)} onCheckedChange={() => toggleLead(l.id)} data-testid={`outreach-check-${l.id}`}/>
                      ) : (
                        <div className="w-4 h-4"/>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`hub-pill ${statusColor} text-[10px]`}>{l.status}</span>
                          <span className="font-mono text-xs truncate">{l.email}</span>
                          {l.opens > 0 && <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Eye size={10}/>{l.opens}</span>}
                          {l.clicks > 0 && <span className="text-[10px] hub-coral flex items-center gap-0.5"><MousePointerClick size={10}/>{l.clicks}</span>}
                        </div>
                        {l.source_url && <div className="text-[10px] text-[#5C6B7D] truncate mt-0.5">Fuente: {l.source_url}</div>}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeLead(l.id)} className="text-red-500 h-7 w-7 p-0" data-testid={`outreach-del-${l.id}`}>
                        <Trash2 size={12}/>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-[10px] text-[#5C6B7D] italic mt-3">
              GDPR/LSSI-CE: solo escribes a emails corporativos públicos, cada envío incluye link de baja y datos identificativos. Los opt-outs quedan registrados y no vuelven a recibir envíos.
            </p>
          </TabsContent>

          <TabsContent value="payouts" className="mt-4">
            <div className="hub-card p-5 mb-3 bg-[#FF6B57]/5 border border-[#FF6B57]/20">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-[#FF6B57] flex-shrink-0 mt-0.5" size={18}/>
                <div className="text-sm text-[#0B1D3A]">
                  <strong>Red de seguridad activa.</strong> Cuando el propietario aprueba un trabajo pero el profesional aún no tiene Stripe conectado, el dinero se retiene aquí. Nunca se pierde. Realiza la transferencia manual al IBAN del profesional y marca la solicitud como pagada.
                </div>
              </div>
            </div>
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {pendingPayouts.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">✓ No hay payouts manuales pendientes. Todo está automatizado.</div>}
              {pendingPayouts.map(p => (
                <div key={p.id} className="p-4" data-testid={`admin-payout-${p.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="hub-pill bg-[#FF6B57] text-white">Payout pendiente</span>
                    <span className="hub-pill hub-sand text-[#0B1D3A]">{p.service_type}</span>
                    {p.pro_stripe_connected ? <span className="hub-pill bg-emerald-100 text-emerald-700">Stripe OK ahora</span> : <span className="hub-pill bg-amber-100 text-amber-800">Sin Stripe conectado</span>}
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-sm">{p.final_price?.toFixed(2)}€ · {p.port}</div>
                      <div className="text-xs text-[#5C6B7D] truncate">
                        Pro: <strong>{p.pro_name}</strong> ({p.pro_email}) · Req <code>{p.id.slice(0, 8)}</code>
                      </div>
                      <div className="text-xs text-[#5C6B7D]">Motivo: {p.payout_pending_reason || "—"} · Aprobado {p.completed_at ? new Date(p.completed_at).toLocaleString("es-ES") : ""}</div>
                    </div>
                    <Button size="sm" onClick={() => markPayoutPaid(p.id)} className="hub-btn-primary rounded-full" data-testid={`payout-mark-${p.id}`}>
                      Marcar pagado
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <div className="hub-card divide-y divide-[#0B1D3A]/5">
              {activity.length === 0 && <div className="p-10 text-center text-[#5C6B7D]">Sin acciones registradas aún.</div>}
              {activity.map(a => (
                <div key={a.id} className="p-3 flex items-center gap-3" data-testid={`admin-act-${a.id}`}>
                  <Activity size={14} className="text-[#5C6B7D]"/>
                  <div className="flex-1">
                    <div className="font-display text-sm"><strong>{a.actor_name}</strong> · {a.action}</div>
                    <div className="text-xs text-[#5C6B7D]">{new Date(a.created_at).toLocaleString("es-ES")}{a.detail ? ` · ${a.detail}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
